import { useQuery } from '@apollo/client/react';
import { DocumentNode } from 'graphql';

/**
 * Generic Apollo hook for fetching customer data with any GraphQL query
 * 
 * Usage examples:
 * 
 * // Simple query without variables
 * const { data, loading, error } = useGetCustomer(GET_BASIC_CUSTOMER_DATA_QUERY)
 * 
 * // With type safety
 * const { data: customerBasicData, loading, error } = useGetCustomer<BasicCustomerDataResponse>(GET_BASIC_CUSTOMER_DATA_QUERY)
 * 
 * // With pagination variables for cards query
 * const { data: cardsData, loading, error } = useGetCustomer<CardsCustomerDataResponse>(
 *   GET_CARDS_CUSTOMER_DATA_QUERY,
 *   { first: 49, after: null, before: null, last: null }
 * )
 * 
 * // Custom pagination
 * const { data: cardsData } = useGetCustomer<CardsCustomerDataResponse>(
 *   GET_CARDS_CUSTOMER_DATA_QUERY,
 *   { first: 10, after: 'cursor-value' }
 * )
 * 
 * // Conditional fetching (skip when props available)
 * const { data: cardsData } = useGetCustomer<CardsCustomerDataResponse>(
 *   GET_CARDS_CUSTOMER_DATA_QUERY,
 *   { first: 49 },
 *   { skip: !!propsData }
 * )
 * 
 * @param gqlQuery - GraphQL query constant (e.g., GET_BASIC_CUSTOMER_DATA_QUERY)
 * @param variables - Optional variables for the query (e.g., pagination params)
 * @param options - Optional Apollo query options (e.g., { skip: boolean })
 * @returns Object with data, loading, and error states
 */
export const useGetCustomer = <T = any,>(
	gqlQuery: DocumentNode,
	variables?: Record<string, any>,
	options?: { skip?: boolean; fetchPolicy?: 'cache-first' | 'network-only' | 'cache-only' | 'no-cache' }
) => {
	const { data, loading, error, refetch } = useQuery<T>(gqlQuery, {
		variables,
		skip: options?.skip,
		// Use cache-first to prevent request cancellation in StrictMode, but allow override for pagination
		fetchPolicy: options?.fetchPolicy || 'cache-first',
		errorPolicy: 'all',
	});

	return {
		data,
		loading,
		error,
		refetch,
	};
};